// The subscribe flow of the push notifications (player-reach.md §3.5,
// R1301). The ALERTS card of the hub SETTINGS panel (`ui/hub/panels/
// alerts.ts`) draws what this controller holds and calls it on a press.
//
// The flow:
//
//   1. On load it reads the support of the browser, the permission, the
//      public key (`GET /api/push/key`), the subscription of this browser
//      and what the meta server stores for it (`GET /api/push?endpoint=`).
//   2. The FIRST class the player turns on asks the permission of the
//      browser, registers the service worker, subscribes the push manager
//      and sends `PUT /api/push`. The permission call is the first thing
//      the press does, because a browser grants the prompt only inside the
//      gesture.
//   3. Every later change of a class, of the quiet hours or of the night
//      switch sends `PUT /api/push` again.
//   4. Turning the LAST class off sends `DELETE /api/push` and unsubscribes.
//
// iOS gives web push only to a web app that the person added to the home
// screen. In a Safari tab the card therefore shows that step and keeps the
// switches disabled (`pushSupport`).
//
// The iOS store app has no web push. Its app shell asks for the permission
// and answers the APNs token over the bridge (`push.register`,
// `push.state`), and the card stores the token with `kind: "apns"` in
// place of a web push endpoint (mobile-apps.md §7, R2927). A store app
// never shows the home-screen step. The Android store app keeps web push.
//
// The controller touches no DOM. `PushEnv` is everything it reads from the
// browser and the meta server, so a test replaces all of it.

import {
  ApiError, CODE_PUSH_OFF, PUSH_KIND_APNS, deleteApnsPush, deletePush, getApnsPush, getPush, getPushKey,
  messageOf, putApnsPush, putPush,
  type ApnsAddress, type ApnsSubscriptionBody, type PushPrefs, type PushState, type PushSubscriptionBody,
} from '../net/api';
import { PUSH_CLASSES, type PushClass } from '../gen/balance';
import { SHELL_ANSWER, bridgeAsk, bridgeHas, pageWindow, type BridgeWindow } from '../platform/bridge';
import { storeApp, type StoreApp } from '../platform/platform';
import { bytesOfBase64 } from '../util/base64';
import { str } from '../net/json';

/** The address of the service worker. It must stand at the site root,
 *  because the scope of a worker cannot reach above its own folder. */
export const WORKER_URL = '/sw.js';

/** The default quiet hours in local time (player-reach.md §3.2). The meta
 *  server stores what this browser sends, so the default is the card's. */
export const DEFAULT_QUIET_START = '23:00';
export const DEFAULT_QUIET_END = '07:00';

/** The preferences of the card before the time zone joins them. */
export interface AlertPrefs {
  classes: PushClass[];
  quietStart: string;
  quietEnd: string;
  siegeAtNight: boolean;
}

/** The preferences a browser that never subscribed starts with: every
 *  class off, the quiet hours 23:00 to 07:00, the night switch off. */
export function defaultPrefs(): AlertPrefs {
  return {
    classes: [], quietStart: DEFAULT_QUIET_START, quietEnd: DEFAULT_QUIET_END,
    siegeAtNight: false,
  };
}

/** What the browser can do. */
export type PushSupport =
  /** Web push works here. */
  | 'ok'
  /** iOS or iPadOS outside a home-screen web app: add the page first. */
  | 'ios-install'
  /** The browser has no web push. */
  | 'unsupported';

/** The parts of a push subscription the flow reads. */
export interface PushSub {
  endpoint: string;
  toJSON(): { keys?: Record<string, string> };
  unsubscribe(): Promise<boolean>;
}

/** The parts of a service worker registration the flow reads. */
export interface PushReg {
  pushManager: {
    getSubscription(): Promise<PushSub | null>;
    subscribe(o: { userVisibleOnly: boolean; applicationServerKey: Uint8Array }): Promise<PushSub>;
  };
}

/** The push of the iOS app shell (mobile-apps.md §5, §7): the two ops of
 *  the bridge and the APNs half of `/api/push`. */
export interface ShellPush {
  /** `push.state`: the permission and the token, without a prompt. */
  state(): Promise<{ granted: boolean; token: string; env: string }>;
  /** `push.register`: the token, or null for `push.denied`. */
  register(): Promise<ApnsAddress | null>;
  get(token: string): Promise<PushState>;
  put(body: ApnsSubscriptionBody): Promise<unknown>;
  remove(token: string): Promise<unknown>;
}

/** Everything the flow reads from the browser and the meta server. */
export interface PushEnv {
  /** The push of the iOS app shell, where its bridge knows both push ops. */
  shell?: ShellPush;
  /** The store app the page runs in; absent or null is a browser. */
  storeApp?: StoreApp | null;
  /** The Notification interface, or undefined when the browser has none. */
  notification?: {
    readonly permission: string;
    requestPermission(): Promise<string>;
  };
  /** `navigator.serviceWorker`, or undefined when the browser has none. */
  container?: {
    register(url: string, o: { scope: string }): Promise<unknown>;
    getRegistration(scope: string): Promise<PushReg | undefined>;
    readonly ready: Promise<PushReg>;
  };
  /** Whether `PushManager` exists. */
  pushManager: boolean;
  /** `navigator.userAgent`, `navigator.platform` and `maxTouchPoints`. */
  userAgent: string;
  platform: string;
  maxTouchPoints: number;
  /** Whether the page runs as a home-screen web app. */
  standalone: boolean;
  /** The IANA time zone of the browser. */
  timeZone(): string;
  api: {
    getPushKey(): Promise<string>;
    getPush(endpoint: string): Promise<PushState>;
    putPush(body: PushSubscriptionBody): Promise<unknown>;
    deletePush(endpoint: string): Promise<unknown>;
  };
}

/**
 * True for iOS and iPadOS. An iPad asks for the desktop page by default
 * and names itself `MacIntel`, so a Mac platform with a touch screen is an
 * iPad: no Mac has one.
 */
export function isIos(userAgent: string, platform: string, maxTouchPoints: number): boolean {
  if (/iPad|iPhone|iPod/.test(userAgent)) return true;
  return platform === 'MacIntel' && maxTouchPoints > 1;
}

/** What the browser can do (`PushSupport`). iOS is asked first, because a
 *  Safari tab lacks the push manager that the web app has. */
export function pushSupport(env: PushEnv): PushSupport {
  if (env.shell) return 'ok';
  // A store app never shows the home-screen step (R2927).
  if (isIos(env.userAgent, env.platform, env.maxTouchPoints) && !env.standalone && !env.storeApp) {
    return 'ios-install';
  }
  if (!env.notification || !env.container || !env.pushManager) return 'unsupported';
  return 'ok';
}

/** The push of the iOS app shell over the bridge in `w`, or undefined
 *  where the bridge does not know both push ops. */
export function shellPush(
  w: BridgeWindow | null = pageWindow(),
): ShellPush | undefined {
  if (!bridgeHas('push.register', w) || !bridgeHas('push.state', w)) return undefined;
  return {
    state: async () => {
      const a = await bridgeAsk('push.state', [SHELL_ANSWER.pushState], {}, w);
      return { granted: a?.granted === true, token: str(a?.token), env: str(a?.env) };
    },
    register: async () => {
      const a = await bridgeAsk('push.register', [SHELL_ANSWER.pushToken, SHELL_ANSWER.pushDenied], {}, w);
      const token = a?.op === SHELL_ANSWER.pushToken ? str(a.token) : '';
      return token === '' ? null : { token, env: str(a?.env) };
    },
    get: getApnsPush,
    put: putApnsPush,
    remove: deleteApnsPush,
  };
}

/** The browser this page runs in, as a `PushEnv`. */
export function browserEnv(): PushEnv {
  const nav = globalThis.navigator;
  const w = globalThis as unknown as {
    Notification?: PushEnv['notification'];
    PushManager?: unknown;
    matchMedia?: (q: string) => { matches: boolean };
  };
  const standalone = (w.matchMedia?.('(display-mode: standalone)').matches ?? false)
    || (nav as { standalone?: boolean } | undefined)?.standalone === true;
  return {
    shell: shellPush(),
    storeApp: storeApp(),
    notification: w.Notification,
    container: nav?.serviceWorker as unknown as PushEnv['container'],
    pushManager: w.PushManager !== undefined,
    userAgent: nav?.userAgent ?? '',
    platform: nav?.platform ?? '',
    maxTouchPoints: nav?.maxTouchPoints ?? 0,
    standalone,
    timeZone: () => Intl.DateTimeFormat().resolvedOptions().timeZone,
    api: { getPushKey, getPush, putPush, deletePush },
  };
}

/** A base64url key as the bytes `applicationServerKey` takes. */
export function keyBytes(b64url: string): Uint8Array {
  return bytesOfBase64(b64url.replace(/-/g, '+').replace(/_/g, '/')
    + '='.repeat((4 - (b64url.length % 4)) % 4));
}

/** A clock time `HH:MM`, 00:00 to 23:59. */
function isClock(v: string): boolean {
  return /^([01][0-9]|2[0-3]):[0-5][0-9]$/.test(v);
}

/** The classes of a list that `PUSH_CLASSES` knows, in its order. */
function orderedClasses(list: readonly string[]): PushClass[] {
  return PUSH_CLASSES.filter((c) => list.includes(c));
}

/** The preferences half of a `PUT /api/push` body, both kinds. */
function prefsBody(prefs: AlertPrefs, timeZone: string): PushPrefs {
  return {
    classes: [...prefs.classes],
    quietStart: prefs.quietStart,
    quietEnd: prefs.quietEnd,
    timeZone,
    siegeAtNight: prefs.siegeAtNight,
  };
}

/** The body of `PUT /api/push`, exactly in the shape of §3.8. */
function putBody(sub: PushSub, prefs: AlertPrefs, timeZone: string): PushSubscriptionBody {
  const keys = sub.toJSON().keys ?? {};
  return {
    endpoint: sub.endpoint,
    keys: { p256dh: keys.p256dh ?? '', auth: keys.auth ?? '' },
    ...prefsBody(prefs, timeZone),
  };
}

/** The body of `PUT /api/push` for an APNs token (mobile-apps.md §7). */
function apnsBody(a: ApnsAddress, prefs: AlertPrefs, timeZone: string): ApnsSubscriptionBody {
  return { kind: PUSH_KIND_APNS, token: a.token, env: a.env, ...prefsBody(prefs, timeZone) };
}

/** Where the card stands. */
export type AlertStatus =
  /** The load runs. */
  | 'checking'
  /** The switches work. */
  | 'ready'
  /** iOS outside the web app: the card shows the home-screen step. */
  | 'ios-install'
  /** The browser has no web push. */
  | 'unsupported'
  /** The server has no push, or its key did not answer. */
  | 'unavailable';

/** The controller of the ALERTS card. */
export class PushAlerts {
  status: AlertStatus = 'checking';
  prefs: AlertPrefs = defaultPrefs();
  /** True when the browser refused the permission. */
  denied = false;
  /** True while a call is in flight. A press then does nothing. */
  busy = false;
  /** The last failure, as a person reads it, or ''. */
  err = '';
  private sub: PushSub | null = null;
  /** The APNs token this install stored, in the iOS store app. */
  private apns: ApnsAddress | null = null;
  private key = '';
  /** True when the server answered `push_off`: a later load does not ask
   *  again, because the host has no key to give. */
  private off = false;
  /** Counts the loads and the presses. A load whose count is stale when
   *  its answer comes does not write: a press in between is newer. */
  private gen = 0;

  constructor(private readonly env: PushEnv, private readonly changed: () => void) {}

  /** Whether the switches take a press. */
  get enabled(): boolean {
    return this.status === 'ready' && !this.denied && !this.busy;
  }

  /** Whether this browser holds a subscription now. */
  get subscribed(): boolean {
    return this.sub !== null || this.apns !== null;
  }

  /** True when the iOS app shell carries the push: the card names the
   *  settings of iOS, not those of a browser. */
  get native(): boolean {
    return this.env.shell !== undefined;
  }

  /** Reads what the browser and the server hold. The card calls it each
   *  time the panel opens. */
  async load(): Promise<void> {
    const support = pushSupport(this.env);
    if (support !== 'ok') {
      this.settle(support);
      return;
    }
    if (this.off) {
      this.settle('unavailable');
      return;
    }
    if (this.env.shell) {
      await this.loadShell(this.env.shell);
      return;
    }
    this.denied = this.env.notification?.permission === 'denied';
    const gen = ++this.gen;
    try {
      this.key = await this.env.api.getPushKey();
    } catch (err) {
      // The ONE place that compares the code (R1300). Every refusal shows
      // the same sentence; `push_off` alone is kept for the page's life.
      if (err instanceof ApiError && err.code === CODE_PUSH_OFF) this.off = true;
      this.settle('unavailable');
      return;
    }
    try {
      const reg = await this.env.container?.getRegistration('/');
      const sub = (await reg?.pushManager.getSubscription()) ?? null;
      const state = sub ? await this.env.api.getPush(sub.endpoint) : undefined;
      if (gen !== this.gen) return;
      this.sub = sub;
      if (state?.subscribed) this.prefs = fromServer(state);
      else if (sub) this.prefs = { ...this.prefs, classes: [] };
      this.err = '';
    } catch (err) {
      if (gen !== this.gen) return;
      this.err = messageOf(err);
    }
    this.settle('ready');
  }

  /**
   * The load in the iOS app shell: `push.state` names the permission and
   * the token, and the meta server answers what it stores for the token.
   * `push.state` names no refusal, so a refused permission shows when a
   * press asks again and the app shell answers `push.denied`.
   */
  private async loadShell(shell: ShellPush): Promise<void> {
    const gen = ++this.gen;
    try {
      const st = await shell.state();
      if (gen !== this.gen) return;
      const state = st.granted && st.token !== '' ? await shell.get(st.token) : undefined;
      if (gen !== this.gen) return;
      this.apns = state?.subscribed ? { token: st.token, env: st.env } : null;
      if (state?.subscribed) this.prefs = fromServer(state);
      else this.prefs = { ...this.prefs, classes: [] };
      this.err = '';
    } catch (err) {
      if (gen !== this.gen) return;
      this.err = messageOf(err);
    }
    this.settle('ready');
  }

  /** The load ends at `status`, and the card draws it. */
  private settle(status: AlertStatus): void {
    this.status = status;
    this.changed();
  }

  /** Turns one class on or off. */
  setClass(c: PushClass, on: boolean): void {
    if (!this.enabled) return;
    const has = this.prefs.classes.includes(c);
    if (has === on) return;
    const next: AlertPrefs = {
      ...this.prefs,
      classes: orderedClasses(on ? [...this.prefs.classes, c] : this.prefs.classes.filter((x) => x !== c)),
    };
    if (next.classes.length === 0) {
      this.run(() => this.disable(next));
    } else if (!this.subscribed && this.env.shell) {
      const asked = this.env.shell.register();
      this.run(() => this.enableShell(next, asked));
    } else if (!this.subscribed) {
      // The prompt must open inside the gesture, so the call comes before
      // the first await of the press.
      const asked = this.env.notification!.requestPermission();
      this.run(() => this.enable(next, asked));
    } else {
      this.run(() => this.store(next));
    }
  }

  /** Sets the quiet hours. A value that is no clock time does nothing. */
  setQuiet(start: string, end: string): void {
    if (!isClock(start) || !isClock(end)) return;
    if (start === this.prefs.quietStart && end === this.prefs.quietEnd) return;
    this.change({ ...this.prefs, quietStart: start, quietEnd: end });
  }

  /** Sets the night switch of the siege class. */
  setNight(on: boolean): void {
    if (on === this.prefs.siegeAtNight) return;
    this.change({ ...this.prefs, siegeAtNight: on });
  }

  /** A change of the hours or of the night switch: stored at once while a
   *  subscription stands, and kept for the first subscribe otherwise. */
  private change(next: AlertPrefs): void {
    if (this.status !== 'ready' || this.busy) return;
    if (!this.subscribed) {
      this.prefs = next;
      this.changed();
      return;
    }
    this.run(() => this.store(next));
  }

  private run(step: () => Promise<void>): void {
    this.gen++;
    this.busy = true;
    this.err = '';
    this.changed();
    void step().catch((err: unknown) => {
      this.err = messageOf(err);
    }).finally(() => {
      this.busy = false;
      this.changed();
    });
  }

  private async enable(next: AlertPrefs, asked: Promise<string>): Promise<void> {
    const perm = await asked;
    if (perm !== 'granted') {
      this.denied = true;
      return;
    }
    this.denied = false;
    const container = this.env.container!;
    await container.register(WORKER_URL, { scope: '/' });
    const reg = await container.ready;
    // `load` read the key before it set the status that lets a press in.
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true, applicationServerKey: keyBytes(this.key),
    });
    await this.env.api.putPush(putBody(sub, next, this.env.timeZone()));
    this.sub = sub;
    this.prefs = next;
  }

  /** The first class in the iOS app shell: the token of `push.register`,
   *  stored with the preferences, or the refused state. */
  private async enableShell(next: AlertPrefs, asked: Promise<ApnsAddress | null>): Promise<void> {
    const got = await asked;
    if (got === null) {
      this.denied = true;
      return;
    }
    this.denied = false;
    await this.env.shell!.put(apnsBody(got, next, this.env.timeZone()));
    this.apns = got;
    this.prefs = next;
  }

  private async store(next: AlertPrefs): Promise<void> {
    if (this.apns) await this.env.shell!.put(apnsBody(this.apns, next, this.env.timeZone()));
    else await this.env.api.putPush(putBody(this.sub!, next, this.env.timeZone()));
    this.prefs = next;
  }

  private async disable(next: AlertPrefs): Promise<void> {
    if (this.apns) {
      await this.env.shell!.remove(this.apns.token);
      this.apns = null;
    }
    const sub = this.sub;
    if (sub) {
      await this.env.api.deletePush(sub.endpoint);
      await sub.unsubscribe();
      this.sub = null;
    }
    this.prefs = next;
  }
}

/** The preferences a stored subscription holds. A value the server sends
 *  that the card cannot read takes the default. */
function fromServer(s: PushPrefs): AlertPrefs {
  return {
    classes: orderedClasses(s.classes),
    quietStart: isClock(s.quietStart) ? s.quietStart : DEFAULT_QUIET_START,
    quietEnd: isClock(s.quietEnd) ? s.quietEnd : DEFAULT_QUIET_END,
    siegeAtNight: s.siegeAtNight,
  };
}
